import { FILE_STATUS_UNPROCESSED } from "constants/FileConstants";
import type {
  ClaimsDataType,
  CreateFileData,
  FileType,
  FileValidation,
  PaginatedResponse,
} from "types/DataTypes.ts";

import { api } from "./api.ts";

export const fileClaimNumbersAPI = api.injectEndpoints({
  endpoints: (build) => ({
    createClaimNumber: build.mutation<
      FileType,
      {
        data: { number: string; file: string; date_of_injury_id: string };
      }
    >({
      query: ({ data }) => ({
        url: "api/v1/claim_numbers/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Files"],
    }),
    patchClaimNumberById: build.mutation<
      FileType,
      {
        id: number;
        data: { number: string; file: string; date_of_injury_id: string };
      }
    >({
      query: ({ id, data }) => ({
        url: `api/v1/claim_numbers/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Files"],
    }),
    deleteClaimNumber: build.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `api/v1/claim_numbers/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Files"],
    }),
  }),
  overrideExisting: false,
});

export const fileAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getFiles: build.query<
      PaginatedResponse<FileType>,
      {
        searchTerm?: string | null;
        filters?: Record<string, unknown>;
        sort_by?: string;
        excludeSystemUserId?: number;
      }
    >({
      query: ({
        searchTerm,
        filters = {},
        sort_by = "-id",
        excludeSystemUserId,
      }) => ({
        url: "api/v1/files/",
        method: "GET",
        params: {
          ...(searchTerm && { filter: searchTerm }),
          ...(sort_by && { sort_by }),
          ...(excludeSystemUserId && { not_assignee: excludeSystemUserId }),
          ...filters,
        },
      }),
      providesTags: ["Files"],
    }),
    getIntakeFiles: build.query<
      PaginatedResponse<FileType>,
      {
        searchTerm?: string | null;
        filters?: Record<string, unknown>;
        sort_by?: string;
      }
    >({
      query: ({ searchTerm, filters = {}, sort_by = "-id" }) => ({
        url: "api/v1/files/",
        method: "GET",
        params: {
          ...(searchTerm && { filter: searchTerm }),
          ...(sort_by && { sort_by }),
          // intake is every file that has not been opened yet, whatever its
          // source -- not just the ones submitted through the intake site
          status: FILE_STATUS_UNPROCESSED,
          ...filters,
        },
      }),
      providesTags: ["Files"],
    }),
    getFileById: build.query<FileType, { id: string }>({
      query: ({ id }) => ({
        url: `api/v1/files/${id}/?detail=true`,
        method: "GET",
      }),
      providesTags: (result, error, { id }) => [
        { type: "Files", id },
        "FileValidation",
      ],
    }),
    createFile: build.mutation<
      FileType,
      { data: FormData; useDetail?: boolean }
    >({
      query: ({ data, useDetail }) => ({
        url: `api/v1/files/?detail=${useDetail ? useDetail : false}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Files"],
    }),
    getFileValidationById: build.query<FileValidation, { id: string }>({
      query: ({ id }) => ({
        url: `api/v1/files/${id}/validate/`,
        method: "GET",
      }),
      providesTags: ["Files", "FileValidation"],
    }),
    patchFileById: build.mutation<
      FileType,
      {
        id: string;
        data: Partial<CreateFileData>;
        useDetail?: boolean;
      }
    >({
      query: ({ id, data, useDetail }) => ({
        url: `api/v1/files/${id}/?detail=${useDetail ? useDetail : false}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Files", "DOI"],
    }),
    patchFileStatusById: build.mutation<
      FileType,
      { id: string | number; status: string }
    >({
      query: ({ id, status }) => ({
        url: `api/v1/files/${id}/change_status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Files", "Tasks"],
    }),
    deleteFileById: build.mutation<FileType, { id: string | number }>({
      query: ({ id }) => ({
        url: `api/v1/files/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Files"],
    }),
    mergeFile: build.mutation<
      FileType,
      { id: string | number; targetFileId: number }
    >({
      // merges (and archives) file `id` into `targetFileId`; responds with
      // the target file so the caller can navigate straight to it
      query: ({ id, targetFileId }) => ({
        url: `api/v1/files/${id}/merge/`,
        method: "POST",
        body: { target_file_id: targetFileId },
      }),
      // broad on purpose: the merge touches the target's notes, documents
      // and tasks as well as both files, and the caller navigates away from
      // the (now archived) source immediately afterwards
      invalidatesTags: ["Files", "Tasks", "Documents", "Notes"],
    }),
    uploadClaimsImportForFile: build.mutation({
      query: ({ id, file }) => ({
        url: `api/v1/files/${id}/claim_import/`,
        method: "POST",
        body: file,
      }),
      invalidatesTags: ["Documents"],
    }),
    createFileClaimAssociation: build.mutation({
      query: ({ id, claimId }) => ({
        url: `api/v1/files/${id}/associate_claim/`,
        method: "POST",
        body: { claim: claimId },
      }),
      invalidatesTags: ["Files", "Claims"],
    }),
    deleteFileClaimAssociation: build.mutation({
      query: ({ id, claimId }) => ({
        url: `api/v1/files/${id}/unassociate_claim/`,
        method: "DELETE",
        body: { claim: claimId },
      }),
      invalidatesTags: ["Files", "Claims"],
    }),
    getSuggestedClaimAssociations: build.query<
      ClaimsDataType[],
      { id: number }
    >({
      query: ({ id }) => ({
        url: `api/v1/files/${id}/suggested_claim_associations/`,
        method: "GET",
      }),
      providesTags: ["SuggestedClaimLinks"],
    }),
    getFileAuditHistory: build.query({
      query: ({ id }) => ({
        url: `api/v1/files/${id}/audit_trail/`,
        method: "GET",
      }),
      providesTags: ["Files"],
    }),
    sendAcknowledgementLetter: build.mutation<
      { referral_acknowledgement_sent_at: string },
      {
        id: string | number;
        to_email: string;
        subject: string;
        body: string;
        cc_emails?: string[];
      }
    >({
      query: ({ id, to_email, subject, body, cc_emails }) => ({
        url: `api/v1/files/${id}/send-acknowledgement-letter/`,
        method: "POST",
        body: { to_email, subject, body, cc_emails },
      }),
      invalidatesTags: ["Files"],
    }),
  }),
  overrideExisting: false,
});

export const {
  usePatchClaimNumberByIdMutation,
  useDeleteClaimNumberMutation,
  useCreateClaimNumberMutation,
} = fileClaimNumbersAPI;

export const {
  useGetFilesQuery,
  useGetIntakeFilesQuery,
  useCreateFileMutation,
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
  usePatchFileStatusByIdMutation,
  useGetFileValidationByIdQuery,
  useDeleteFileByIdMutation,
  useMergeFileMutation,
  useCreateFileClaimAssociationMutation,
  useDeleteFileClaimAssociationMutation,
  useUploadClaimsImportForFileMutation,
  useGetSuggestedClaimAssociationsQuery,
  useGetFileAuditHistoryQuery,
  useSendAcknowledgementLetterMutation,
} = fileAPI;
